import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { Calendar } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { applyTableState, useTableState, DataTableToolbar } from '@/components/blocks/data-table'
import { ControlBar, ControlBarGroup } from '@/components/ui/ControlBar'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/Toast'
import { LEADERBOARD_COLUMNS, LeaderboardTable } from '@/components/leaderboard'
import { ConfirmDialog, type ConfirmRequest, PageHeader, StatCard } from '@/components/shared'
import { calendarDate, count } from '@/lib'
import { LEADERBOARD } from '@/fixtures'
import type { LeaderboardRow, Stat } from '@/models'

const TABLE_INITIAL = { pageSize: 10, sortKey: 'rank', sortDir: 'asc' as const }

interface SeasonInfo {
  id: string
  name: string
  startDate: string
  endDate: string
  status: 'Active' | 'Ended'
}

/* ISO in the fixture, formatted at the edge — 01/08/2026 reads as 8 January to
 * half the world. */
const SEASONS: SeasonInfo[] = [
  {
    id: 's14',
    name: 'Season 14',
    startDate: '2026-08-01',
    endDate: '2026-08-31',
    status: 'Active',
  },
  { id: 's13', name: 'Season 13', startDate: '2026-07-01', endDate: '2026-07-31', status: 'Ended' },
  { id: 's12', name: 'Season 12', startDate: '2026-06-01', endDate: '2026-06-30', status: 'Ended' },
]

export function LeaderboardPage() {
  const { toast } = useToast()
  const [seasonId, setSeasonId] = useState<string>(SEASONS[0].id)
  const [rows, setRows] = useState<LeaderboardRow[]>(LEADERBOARD)
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null)
  const { state, setState } = useTableState(TABLE_INITIAL)

  const selectedSeason = SEASONS.find((s) => s.id === seasonId) ?? SEASONS[0]

  /* Past seasons shift trophy values slightly for fixture demonstration */
  const seasonRows = useMemo(() => {
    if (selectedSeason.id === 's14') return rows
    const offset = selectedSeason.id === 's13' ? 120 : 250
    return rows.map((row, i) => ({
      ...row,
      trophies: Math.max(800, row.trophies - offset - (i % 7) * 30),
    }))
  }, [rows, selectedSeason])

  const table = useMemo(
    () => applyTableState(seasonRows, LEADERBOARD_COLUMNS, state),
    [seasonRows, state],
  )

  const stats: Stat[] = useMemo(() => {
    const topPlayer = seasonRows.find((r) => r.rank === 1)
    const avgWinRate = seasonRows.reduce((sum, r) => sum + r.winRate, 0) / (seasonRows.length || 1)
    const highestTrophies = seasonRows.reduce((max, r) => Math.max(max, r.trophies), 0)

    return [
      {
        id: 'top1',
        label: 'Ladder leader',
        value: topPlayer ? `#1 ${topPlayer.username}` : '—',
        delta: topPlayer ? `${count(topPlayer.trophies)} trophies` : '',
        up: true,
      },
      {
        id: 'total',
        label: 'Ranked players',
        value: count(seasonRows.length),
        delta: '+8.3%',
        up: true,
      },
      {
        id: 'winRate',
        label: 'Average win rate',
        value: `${avgWinRate.toFixed(1)}%`,
        delta: '+1.5%',
        up: true,
      },
      {
        id: 'highest',
        label: 'Season highest',
        value: count(highestTrophies),
        delta: 'All-time peak',
        up: true,
      },
    ]
  }, [seasonRows])

  return (
    <div className="space-y-6">
      <PageHeader title="Leaderboard" description="Top of the global ladder this season." />

      {/* Summary Stat Micro-Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.id} stat={stat} />
        ))}
      </div>

      <div className="space-y-3">
        <ControlBar>
          <ControlBarGroup>
            <Select
              aria-label="Season"
              options={SEASONS.map((season) => ({ value: season.id, label: season.name }))}
              value={seasonId}
              onValueChange={setSeasonId}
              triggerClassName="min-w-48 bg-bg-elev"
            />
            <Badge dot variant={selectedSeason.status === 'Active' ? 'success' : 'neutral'}>
              {selectedSeason.status}
            </Badge>
          </ControlBarGroup>

          <div className="text-fg-subtle flex items-center gap-2 text-sm">
            <Calendar aria-hidden className="size-3.5" />
            <span>
              Start:{' '}
              <strong className="text-fg font-medium">
                {calendarDate(selectedSeason.startDate)}
              </strong>
            </span>
            <span aria-hidden>•</span>
            <span>
              End:{' '}
              <strong className="text-fg font-medium">
                {calendarDate(selectedSeason.endDate)}
              </strong>
            </span>
          </div>
        </ControlBar>

        <DataTableToolbar
          state={state}
          onStateChange={setState}
          searchPlaceholder="Search players by username or clan…"
        />

        <LeaderboardTable
          rows={table.rows}
          total={table.total}
          state={state}
          onStateChange={setState}
          onOpen={(row) => router.visit(`/players/${row.id}`)}
          onReset={(row) =>
            setConfirm({
              title: `Reset ${row.username}'s trophies?`,
              description: 'They drop to the season floor of 800 and lose their ladder position.',
              confirmLabel: 'Reset Trophies',
              destructive: true,
              onConfirm: () => {
                setRows((previous) =>
                  previous.map((entry) =>
                    entry.id === row.id ? { ...entry, trophies: 800 } : entry,
                  ),
                )
                toast(`${row.username} reset to 800 trophies`, 'danger')
              },
            })
          }
        />
      </div>

      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </div>
  )
}
